import type { PaperWithRole } from "@/types/paper";

const LATEX_SPECIAL_CHARS: [RegExp, string][] = [
  [/\\/g, "\\textbackslash{}"],
  [/&/g, "\\&"],
  [/%/g, "\\%"],
  [/\$/g, "\\$"],
  [/#/g, "\\#"],
  [/_/g, "\\_"],
  [/\{/g, "\\{"],
  [/\}/g, "\\}"],
];

function escapeLatex(text: string): string {
  let escaped = text;
  for (const [pattern, replacement] of LATEX_SPECIAL_CHARS) {
    escaped = escaped.replace(pattern, replacement);
  }
  return escaped;
}

/**
 * Format authors in BibTeX style: "LastName, FirstName and LastName, FirstName"
 */
function formatAuthors(authors: string[]): string {
  return authors
    .map((author) => {
      const trimmed = author.trim();
      // Already "Last, First" format
      if (trimmed.includes(",")) return trimmed;
      // "First Last" → "Last, First"
      const parts = trimmed.split(/\s+/);
      if (parts.length === 1) return parts[0];
      const last = parts[parts.length - 1];
      const first = parts.slice(0, -1).join(" ");
      return `${last}, ${first}`;
    })
    .join(" and ");
}

/**
 * Generate a citation key: lastname_year_firstword
 */
function generateCiteKey(paper: PaperWithRole): string {
  const lastName =
    paper.authors.length > 0
      ? paper.authors[0]
          .trim()
          .split(/[\s,]+/)
          .pop()
          ?.toLowerCase()
          .replace(/[^a-z]/g, "") ?? "unknown"
      : "unknown";
  const year = paper.year ?? "nd";
  const firstWord =
    paper.title
      .split(/\s+/)
      .find((w) => w.length > 3)
      ?.toLowerCase()
      .replace(/[^a-z]/g, "") ?? "paper";
  return `${lastName}_${year}_${firstWord}`;
}

export function paperToBibTeXEntry(
  paper: PaperWithRole,
  usedKeys: Map<string, number>,
): string {
  let baseKey = generateCiteKey(paper);
  const count = usedKeys.get(baseKey) ?? 0;
  usedKeys.set(baseKey, count + 1);
  const citeKey = count > 0 ? `${baseKey}${String.fromCharCode(96 + count)}` : baseKey;

  const fields: string[] = [];

  if (paper.authors.length > 0) {
    fields.push(`  author = {${formatAuthors(paper.authors)}}`);
  }
  fields.push(`  title = {${escapeLatex(paper.title)}}`);
  if (paper.journal) {
    fields.push(`  journal = {${escapeLatex(paper.journal)}}`);
  }
  if (paper.year != null) {
    fields.push(`  year = {${paper.year}}`);
  }
  if (paper.doi) {
    fields.push(`  doi = {${paper.doi}}`);
  }
  if (!paper.doi && paper.url) {
    fields.push(`  url = {${paper.url}}`);
  }
  if (paper.abstract) {
    fields.push(`  abstract = {${escapeLatex(paper.abstract)}}`);
  }

  return `@article{${citeKey},\n${fields.join(",\n")}\n}`;
}

export function exportPapersToBibTeX(
  papers: PaperWithRole[],
  filename: string,
): void {
  const usedKeys = new Map<string, number>();
  const entries = papers.map((p) => paperToBibTeXEntry(p, usedKeys));
  const bibtex = entries.join("\n\n") + "\n";
  const blob = new Blob([bibtex], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
